import { useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import Spinner from "../Components/Spinner";
import ErrorMessage from "../Components/ErrorMessage";
import { TfiFiles } from "react-icons/tfi";
import { MdOutlineCheckCircle, MdOutlineCircle } from "react-icons/md";
import { BiPlus } from "react-icons/bi";
import { CiCircleAlert } from "react-icons/ci";
import NewOrganizationModal from "../Organizations/NewOrganizationModal";
import { useLanguage } from "../Translations/LenguageContext";
import MandatorySign from "../Components/MandatorySign";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const UploadFilesModal = ({
    account_id,
    userData,
    show,
    handleClose,
    fetchRecords,
    path,
    currentFolderId,
    orgData,
    fetchOrgData
}) => {
    const {t} = useLanguage()
    const [loadingSave, setLoadingSave] = useState(false);
    const [errorMessage, setErrorMessage] = useState({ message: "", display: false });
    const [files, setFiles] = useState([]);
    const [openOrgModal, setOpenOrgModal] = useState(false)
    const [organizationIds, setOrganizationIds] = useState(["personal"]);

    const handleOrgSelect = (orgId) => {
        setOrganizationIds(prev => {
            if (orgId === "personal") {
                return ["personal"];
            }

            let updatedIds = prev.filter(id => id !== "personal");

            const alreadySelected = updatedIds.includes(orgId);

            return alreadySelected
                ? updatedIds.filter(id => id !== orgId)
                : [...updatedIds, orgId];
        });
    };

    const MAX_FILES = 10;
    const addFiles = (newFiles) => {
        const fileArray = Array.from(newFiles);

        setFiles((prev) => {
            const combined = [...prev, ...fileArray];

            if (combined.length > MAX_FILES) {
                setErrorMessage({
                    message: t.storage.max_files_upload,
                    display: true,
                });

                return prev;
            } else {
                setErrorMessage({
                    message: "",
                    display: false,
                });
            }

            return combined;
        });
    };

    const handleFileInput = (e) => {
        addFiles(e.target.files);
        e.target.value = null;
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            addFiles(e.dataTransfer.files);
            e.dataTransfer.clearData();
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        if (organizationIds.length == 0) {
            setErrorMessage({ message: t.storage.org_required, display: true });
            return;
        }
        
        if (files.length == 0) {
            setErrorMessage({ message: t.storage.min_files_upload, display: true });
            return;
        }


        setLoadingSave(true);
        setErrorMessage({ message: "", display: false });

        try {
            const formData = new FormData();
            formData.append("account_id", account_id);
            formData.append("path", path);
            formData.append("folder_id", currentFolderId);
            formData.append("organization_ids", organizationIds);

            files.forEach((file) => {
                formData.append("files", file);
            });

            const response = await fetch(`${GEN_BACKEND_LINK}/upload_files`, {
                method: "POST",
                body: formData,
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Something went wrong");
            }

            if (data.message === "OK") {
                if (fetchRecords) await fetchRecords();
                closeModal();
            }
        } catch (error) {
            setErrorMessage({
                message: error.message || "Server error",
                display: true,
            });
        } finally {
            setLoadingSave(false);
        }
    };

    const closeModal = () => {
        handleClose();
        setErrorMessage({ message: "", display: false });
        setLoadingSave(false);
        setOrganizationIds(["personal"])
        setTimeout(() => {
            setFiles([]);
        }, 500);
    };

    const handleClickInput = () => {
        document.getElementById("input_file").click();
    };

    return (
        <Modal show={show} onHide={closeModal}>
            <div className="modal_header">
                <p className="modal_header_title">{t.storage.upload_files}</p>
            </div>

            <input
                type="file"
                multiple
                className="input_files"
                id="input_file"
                onChange={handleFileInput}
                hidden
            />


            <div
                className="modal_main_cont">
                <div className="label_input">
                    <label className="input_label">{t.storage.share_with_org} <MandatorySign message={t.mandatory_field}/></label>
                    <Dropdown autoClose={"outside"}>
                        <Dropdown.Toggle
                            as="div"
                            className="register_form_first fade-in"
                        >
                            {
                                organizationIds.length > 0
                                    ? `${organizationIds.length} ${t.selected}`
                                    : t.select
                            }
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="custom_dropdown_menu">
                            <div className="dropdown_container fade-in">
                                <button
                                    key={"personal"}
                                    onClick={() => handleOrgSelect("personal")}
                                    className="settings_link"
                                >
                                    <i >
                                        {organizationIds.includes("personal")
                                            ? <MdOutlineCheckCircle />
                                            : <MdOutlineCircle />}
                                    </i>
                                    Personal
                                </button>
                                {orgData && orgData.map((org) => (
                                    <button
                                        key={org.id}
                                        onClick={() => handleOrgSelect(org.id)}
                                        className="settings_link"
                                    >
                                        <i >
                                            {organizationIds.includes(org.id)
                                                ? <MdOutlineCheckCircle />
                                                : <MdOutlineCircle />}
                                        </i>
                                        {org.name}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setOpenOrgModal(true)}
                                    className="settings_link"
                                >
                                    <i><BiPlus /></i> {t.create_new}
                                </button>
                            </div>
                        </Dropdown.Menu>

                    </Dropdown>
                    {
                        !organizationIds.includes("personal") &&
                        organizationIds.length > 0 && (
                            <p className="role_warning_text">
                                <i><CiCircleAlert /></i>{" "}
                                {t.storage.share_files_warning}
                            </p>
                        )
                    }
                </div>


                {files.length > 0 && (
                    <div className="file_list_cont">
                        {files.map((file, index) => (
                            <div
                                key={index}
                                className="file_list_item"
                            >
                                <span>{file.name}</span>
                                <button
                                    type="button"
                                    onClick={() => removeFile(index)}
                                    className="remove_file_btn"
                                >
                                    <i className="fa-solid fa-x"></i>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                {files.length > 0 ? (
                    <div className="drop_files_cont_header">
                        <button
                            className="select_files_btn"
                            onClick={handleClickInput}
                        >
                            <TfiFiles /> {t.select} files
                        </button>
                        <span>{files.length} {t.selected}</span>
                    </div>
                ) : (
                    <div
                        className="drop_files_cont"
                        onClick={handleClickInput}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                    >
                        <div className="drop_files_cont_inner">
                            <p className="drop_files_title">{t.storage.upload_files}</p>
                            <p className="drop_files_description">
                                {t.storage.click_drag}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <div className="modal_footer">
                <div className="modal_footer_err_cont">
                    <ErrorMessage err={errorMessage} />
                </div>

                <div className="modal_footer_btn_cont">
                    <button
                        className="modale_save_btn"
                        onClick={handleSave}
                        disabled={loadingSave}
                    >
                        {loadingSave ? <Spinner loading={true} /> : t.save}
                    </button>

                    <button
                        className="modale_close_btn"
                        onClick={closeModal}
                        disabled={loadingSave}
                    >
                        {t.close}
                    </button>
                </div>
            </div>

            <NewOrganizationModal
                account_id={account_id}
                show={openOrgModal}
                handleClose={() => setOpenOrgModal(false)}
                fetchRecords={fetchOrgData}
                userData={userData}
            />
        </Modal>
    );
};

export default UploadFilesModal;